import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { auth, db } from '../firebase' // Import Firebase
import Collapse from 'bootstrap/js/dist/collapse'
import navLogo from '../assets/navLogo.png'
import spearLogo from '../assets/spearLogo.jpeg'
import { loadUserFactionAlertState } from '../utils/factionAlerts'
import { isAdminEmail } from '../utils/adminAccess'

const getAuthFallbackName = (authUser) => (
  authUser?.displayName ||
  authUser?.email?.split('@')[0] ||
  'Profilo'
)

function Header () {
  const [user, setUser] = useState(null)
  const [hasFactionAlerts, setHasFactionAlerts] = useState(false)
  const location = useLocation()
  const collapseRef = useRef(null)
  const bsCollapse = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    // Inizializza il collapse solo una volta
    if (collapseRef.current && !bsCollapse.current) {
      bsCollapse.current = new Collapse(collapseRef.current, {
        toggle: false
      })
    }
    let unsubscribeProfile = null
    const unsubscribe = auth.onAuthStateChanged(async authUser => {
      if (unsubscribeProfile) {
        unsubscribeProfile()
        unsubscribeProfile = null
      }

      if (authUser) {
        const { doc, onSnapshot } = await import('firebase/firestore')
        const fallbackName = getAuthFallbackName(authUser)

        setUser({
          uid: authUser.uid,
          email: authUser.email,
          username: fallbackName,
          profileImageUrl: authUser.photoURL || null
        })

        try {
          const docRef = doc(db, 'users', authUser.uid)
          unsubscribeProfile = onSnapshot(docRef, (docSnap) => {
            const data = docSnap.exists() ? docSnap.data() : {}
            setUser({
              uid: authUser.uid,
              email: authUser.email,
              username: data.username || fallbackName,
              profileImageUrl: data.profileImageUrl || authUser.photoURL || null
            })
          }, (error) => {
            console.error('Errore nel recupero del profilo navbar:', error)
          })
        } catch (error) {
          console.error('Errore nel recupero del profilo navbar:', error)
          setUser({
            uid: authUser.uid,
            email: authUser.email,
            username: fallbackName,
            profileImageUrl: authUser.photoURL || null
          })
        }
      } else {
        setUser(null)
      }
    })

    return () => {
      if (unsubscribeProfile) unsubscribeProfile()
      unsubscribe()
    }
  }, [])

  const navItems = useMemo(() => ([
    { to: '/', icon: 'bi-house-door', label: 'Home' },
    { to: '/ranking', icon: 'bi-trophy', label: 'Classifica' },
    { to: '/shows', icon: 'bi-tv', label: 'Shows' },
    { to: '/stats', icon: 'bi-bar-chart-fill', label: 'Statistiche' },
    { to: '/wrestlers', icon: 'bi-people', label: 'Wrestlers' },
    { to: '/rules', icon: 'bi-journal-text', label: 'Regole' },
    { to: '/premium', icon: 'bi-stars', label: 'Premium' },
    { to: '/news', icon: 'bi-newspaper', label: 'News' },
  ]), [])

  const profileName = user?.username || getAuthFallbackName(user)
  const renderProfileMark = (
    wrapperClassName = '',
    avatarClassName = ''
  ) => (
    <span
      className={`app-header__profile-mark ${
        hasFactionAlerts ? 'has-alerts' : ''
      } ${wrapperClassName}`.trim()}
    >
      {user?.profileImageUrl ? (
        <img
          src={user.profileImageUrl}
          alt=''
          className={`app-header__profile-avatar ${avatarClassName}`.trim()}
        />
      ) : (
        <i
          className={`bi bi-person-circle app-header__profile-icon ${avatarClassName}`.trim()}
        />
      )}
      {hasFactionAlerts && (
        <span
          className='app-attention-dot app-attention-dot--header'
          aria-hidden='true'
        />
      )}
    </span>
  )

  useEffect(() => {
    // Chiudi il menu quando cambia la route
    if (bsCollapse.current && collapseRef.current.classList.contains('show')) {
      bsCollapse.current.hide()
    }
  }, [location])

  useEffect(() => {
    let isMounted = true

    const syncFactionAlerts = async () => {
      if (!user?.uid) {
        if (isMounted) setHasFactionAlerts(false)
        return
      }

      try {
        const alertState = await loadUserFactionAlertState({
          db,
          userId: user.uid
        })
        if (isMounted) {
          setHasFactionAlerts(Boolean(alertState.hasAnyAlerts))
        }
      } catch (error) {
        console.error('Errore nel recupero alert fazioni header:', error)
        if (isMounted) {
          setHasFactionAlerts(false)
        }
      }
    }

    syncFactionAlerts()

    return () => {
      isMounted = false
    }
  }, [location.pathname, user])

  const handleToggleMenu = () => {
    if (!collapseRef.current) return

    if (!bsCollapse.current) {
      bsCollapse.current = new Collapse(collapseRef.current, {
        toggle: false
      })
    }

    bsCollapse.current.toggle()
  }

  const handleLogout = () => {
    auth.signOut()
    navigate('/auth')
  }

  return (
    <>
      <nav className='navbar navbar-expand-lg navbar-dark fixed-top app-header'>
        <div className='container app-header__inner'>
          <Link className='navbar-brand app-header__brand' to='/'>
            <span className='app-header__brand-mark' aria-hidden='true'>
              <img
                src={navLogo}
                alt=''
                className='app-header__logo app-header__logo--main'
              />
            </span>
          <span className='app-header__brand-copy'>
            <span className='app-header__brand-title'>fAntaWreStling</span>
            <span className='app-header__brand-x text-center'>x</span>
            <span className='app-header__brand-subtitle text-center'>Spear alla Edge</span>
          </span>
          </Link>
            <span className='app-header__brand-mark' aria-hidden='true'>
          <Link className='navbar-brand app-header__brand' to='https://www.instagram.com/spear_alla_edge____/'>
              <img
                src={spearLogo}
                alt=''
                className='app-header__logo app-header__logo rounded-5'
              />
          </Link>
            </span>
          <button
            className='navbar-toggler app-header__toggle'
            type='button'
            onClick={handleToggleMenu}
            aria-controls='navbarNav'
            aria-label='Apri menu'
          >
            <span className='navbar-toggler-icon'></span>
          </button>

          <div
            className='collapse navbar-collapse app-header__collapse'
            ref={collapseRef}
            id='navbarNav'
          >
            <ul className='navbar-nav ms-auto app-header__nav'>
              {navItems.map((item) => (
                <li className='nav-item px-2' key={item.to}>
                  <NavLink
                    to={item.to}
                    title={item.label}
                    className={({ isActive }) =>
                      `nav-link app-header__nav-link position-relative ${isActive ? 'active' : ''}`
                    }
                  >
                    <i className={`bi ${item.icon} app-header__nav-icon`} aria-hidden='true' />
                    <span className='app-header__nav-label'>{item.label}</span>
                  </NavLink>
                </li>
              ))}

              {user ? (
                <li className='nav-item px-2'>
                  <NavLink
                    to='/profile'
                  className={({ isActive }) =>
                    `nav-link app-header__nav-link app-header__profile-link position-relative ${isActive ? 'active' : ''}`
                  }
                  title={profileName}
                >
                  {renderProfileMark()}
                  <span className='app-header__nav-label app-header__profile-name'>{profileName}</span>
                </NavLink>
              </li>
              ) : (
                <>
                  <li className='nav-item px-2'>
                    <NavLink
                      to='/auth'
                      className={({ isActive }) =>
                        `nav-link app-header__nav-link position-relative ${isActive ? 'active' : ''}`
                      }
                      title='Accedi/Registrati'
                    >
                      <i className='bi bi-box-arrow-in-right app-header__nav-icon' aria-hidden='true' />
                      <span className='app-header__nav-label'>Accedi/Registrati</span>
                    </NavLink>
                  </li>
                </>
              )}

              <li className='nav-item px-2'>
                <NavLink
                  to='/faq'
                  title='FAQ'
                  className={({ isActive }) =>
                    `nav-link app-header__nav-link position-relative ${isActive ? 'active' : ''}`
                  }
                >
                  <i className='bi bi-question-circle app-header__nav-icon' aria-hidden='true' />
                  <span className='app-header__nav-label'>FAQ</span>
                </NavLink>
              </li>

              {/* Link ADMIN solo per email specifiche */}
              {user && isAdminEmail(user.email) && (
                  <li className='nav-item px-2'>
                    <NavLink
                      to='/admin'
                      title='Admin'
                      className={({ isActive }) =>
                        `nav-link app-header__nav-link position-relative ${isActive ? 'active' : ''}`
                      }
                    >
                      <i className='bi bi-shield-lock app-header__nav-icon' aria-hidden='true' />
                      <span className='app-header__nav-label'>Admin</span>
                    </NavLink>
                  </li>
                )}
              {/* If logged in, show logout button */}
              {user && (
                <li className='nav-item px-2'>
                  <button
                    className='nav-link app-header__nav-link btn btn-link text-danger'
                    onClick={handleLogout}
                    title='Logout'
                  >
                    <i className='bi bi-box-arrow-left app-header__nav-icon' aria-hidden='true'></i>
                    <span className='app-header__nav-label'>Logout</span>
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>
      </nav>

      {/* Barra mobile inferiore */}
      <div className='d-lg-none fixed-bottom mobile-bottom-nav bg-dark'>
        <div className='mobile-bottom-nav__inner'>
          <NavLink
            to='/wrestlers'
            className={({ isActive }) =>
              `nav-link mobile-bottom-nav__link position-relative text-center ${
                isActive ? 'text-white' : 'text-secondary'
              }`
            }
          >
            <i className='bi bi-people d-block mx-auto fs-5'></i>
            <span className='small'>Wrestlers</span>
          </NavLink>

          <NavLink
            to='/ranking'
            className={({ isActive }) =>
              `nav-link mobile-bottom-nav__link position-relative text-center ${
                isActive ? 'text-white' : 'text-secondary'
              }`
            }
          >
            <i className='bi bi-trophy d-block mx-auto fs-5'></i>
            <span className='small'>Classifica</span>
          </NavLink>

          <NavLink
            to='/shows'
            className={({ isActive }) =>
              `nav-link mobile-bottom-nav__link position-relative text-center ${
                isActive ? 'text-white' : 'text-secondary'
              }`
            }
          >
            <i className='bi bi-tv d-block mx-auto fs-5'></i>
            <span className='small'>Shows</span>
          </NavLink>

          <NavLink
            to='/rules'
            className={({ isActive }) =>
              `nav-link mobile-bottom-nav__link position-relative text-center ${
                isActive ? 'text-white' : 'text-secondary'
              }`
            }
          >
            <i className='bi bi-journal-text d-block mx-auto fs-5'></i>
            <span className='small'>Regole</span>
          </NavLink>

          {user ? (
            <NavLink
              to='/profile'
              className={({ isActive }) =>
                `nav-link mobile-bottom-nav__link mobile-bottom-nav__profile position-relative text-center ${
                  isActive ? 'text-white' : 'text-secondary'
                }`
              }
              title={profileName}
            >
              {renderProfileMark(
                'mobile-bottom-nav__profile-mark',
                'mobile-bottom-nav__avatar'
              )}
              <span className='small mobile-bottom-nav__profile-name'>{profileName}</span>
            </NavLink>
          ) : (
            <NavLink
              to='/auth'
              className={({ isActive }) =>
                `nav-link mobile-bottom-nav__link position-relative text-center ${
                  isActive ? 'text-white' : 'text-secondary'
                }`
              }
            >
              <i className='bi bi-person-circle d-block mx-auto fs-5'></i>
              <span className='small'>Accedi</span>
            </NavLink>
          )}
        </div>
      </div>
    </>
  )
}

export default Header
